/**
 * useModules 훅
 *
 * 책임:
 * - 모듈 목록 페칭
 * - 모듈별 "지금 수집" 액션 (runNow)
 * - 모듈 설정 업데이트 (updateModule) + 목록 새로고침
 * - 로딩/에러 상태 관리
 * - 재시도 기능
 *
 * ModulesContainer에서 사용
 */

import { useEffect, useState, useCallback } from 'react';
import * as endpoints from '../api/endpoints';
// 타입은 api/types에서 import (endpoints.ts는 이 타입들을 재수출하지 않음)
import type { ModuleListResponse, ModulePatch } from '../api/types';

export interface UseModulesReturn {
  modules: ModuleListResponse | null;
  loading: boolean;
  error: string | null;
  runNow: (name: string) => Promise<number>; // jobId 반환
  ingest: (key: string, body?: object) => Promise<number>; // 즉시 수집 → jobId 반환
  updateModule: (name: string, patch: ModulePatch) => Promise<void>;
  retry: () => void;
}

export function useModules(token: string | null): UseModulesReturn {
  const [modules, setModules] = useState<ModuleListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 모듈 목록 페칭
  const fetch = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const result = await endpoints.fetchModules();
      setModules(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load modules'
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  // "지금 수집" 액션: 모듈을 즉시 실행 → 생성된 jobId 반환
  const doRunNow = useCallback(async (name: string) => {
    const result = await endpoints.runNow(name);
    return result.jobId;
  }, []);

  // 즉시 수집: /{key}/ingest → 생성된 jobId 반환. body는 수집 옵션(생략 가능).
  const doIngest = useCallback(async (key: string, body?: object) => {
    const result = await endpoints.ingest(key, body);
    return result.jobId;
  }, []);

  // 모듈 설정 업데이트: 부분 업데이트 → 목록 새로고침
  const doUpdateModule = useCallback(
    async (name: string, patch: ModulePatch) => {
      await endpoints.updateModule(name, patch);
      // 업데이트 후 목록을 다시 로드 (서버 상태 반영)
      await fetch();
    },
    [fetch]
  );

  // 컴포넌트 마운트 또는 token 변경 시 페칭
  useEffect(() => {
    fetch();
  }, [fetch]);

  return {
    modules,
    loading,
    error,
    runNow: doRunNow,
    ingest: doIngest,
    updateModule: doUpdateModule,
    retry: fetch,
  };
}
